// Hideo - script editor: readable coloured text. A colour picked with the dark theme (e.g. white) vanishes with the
// light theme and vice versa; black vanishes on the teleprompter. fixContrast marks with data-lowc="light"|"dark" the
// pieces that can't be read on the current background (highlight included): the CSS shows them white or black. Display
// only: cleanHtml and saving drop data-lowc (stripLowc in html.ts), so the saved script doesn't change.
// Ported from the legacy editor.js (golden tests: tests/editor-html.test.ts).
import { contrastOf, cssRgb } from '../../utils/color'

/** Below this contrast the text is practically invisible */
export const LOWC_MIN = 2

/** Colour behind an element: the first non-transparent background going up, else the page's, else the dark theme's */
export function bgBehind(el: Element): number[] {
  for (let n: Element | null = el; n && n.nodeType === 1; n = n.parentElement) {
    const c = cssRgb(getComputedStyle(n).backgroundColor)
    if (c) return c
  }
  return cssRgb(getComputedStyle(document.body).backgroundColor) || [15, 15, 15]
}

/** Marks the coloured pieces of root that can't be read on their background (and clears the old marks) */
export function fixContrast(root: ParentNode | null | undefined): void {
  if (!root || !root.querySelectorAll) return
  root.querySelectorAll('[data-lowc]').forEach(n => n.removeAttribute('data-lowc'))
  // bgBehind with a memory for this pass: the marks only change text colours, so the backgrounds stay the same while
  // looping, and the spans of one paragraph share their ancestors' walk (and the body's colour is read once)
  const seen = new Map<Element, number[]>()
  let page: number[] | null = null
  const bgOf = (el: Element): number[] => {
    const walked: Element[] = []
    let found: number[] | undefined
    for (let n: Element | null = el; n && n.nodeType === 1; n = n.parentElement) {
      found = seen.get(n) ?? cssRgb(getComputedStyle(n).backgroundColor) ?? undefined
      walked.push(n)
      if (found) break
    }
    if (!found) found = page ??= cssRgb(getComputedStyle(document.body).backgroundColor) || [15, 15, 15]
    for (const n of walked) seen.set(n, found)
    return found
  }
  root.querySelectorAll('[style*="color"], font[color]').forEach(n => {
    const fg = cssRgb(getComputedStyle(n).color), bg = bgOf(n)
    if (!fg || !bg) return
    if (contrastOf(fg, bg) >= LOWC_MIN) return
    n.setAttribute('data-lowc', contrastOf([255, 255, 255], bg) >= contrastOf([17, 17, 17], bg) ? 'light' : 'dark')
  })
}

/** A computed CSS colour ("rgb(…)" / "rgba(…)") → "#rrggbb"; '' when transparent or unreadable (the colour bars under
 *  the toolbar "A" and highlighter, "Update style to match the selected text") */
export const rgbHex = (c: unknown): string => {
  const m = String(c || '').match(/[\d.]+/g)
  if (!m || m.length < 3 || (m.length > 3 && +m[3] === 0)) return ''
  return '#' + m.slice(0, 3).map(x => (Math.round(+x)).toString(16).padStart(2, '0')).join('')
}
